import React, { FC, useCallback, useState, useRef } from 'react';
import { ActivityIndicator } from 'react-native';
import { AssetType, Asset, AssetProcessingState, AssetMediaType } from '@packages/models/api';
import { assetUploadsService, httpClient, qmrsService } from '@web/services/singletons';
import { buildErrorMetaData, getMediaUrl } from '@packages/core/utils';
import { Button, Icon, StsIconName, Typography } from '@packages/ui/shared';
import { createUseStyles } from 'react-jss';
import { getPopoverContentLocation } from '../../utils/popover-content-location';
import { Popover } from 'react-tiny-popover';
import { QmrAssetListItem } from '@packages/ui/qmr';
import { Row, Col, Form, FormControlProps } from 'react-bootstrap';
import { Toaster } from '@web/components/toaster/toaster';
import { MAX_ATTACHMENT_COUNT } from '@web/config/constants';
import { uniq, split, set } from 'lodash';
import { useAbortController } from '@packages/core/http';
import { getLogger } from '@packages/core/logging';
import { useDropzone } from 'react-dropzone';
import { usePushNotifications } from '@web/core/hooks';
import { useTranslation } from 'react-i18next';
import {
    AssetUploadInfo,
    useAssetUploadsDispatch,
    assetUploadsActions,
    AssetUploadTaskStatus,
    updateAssetProcessingStatus,
} from '@packages/contexts/asset-uploads';
import {
    useQmrState,
    useQmrDispatch,
    useQmrAssetPolling,
    getQmrAssetDetails,
    QmrAssetDetails,
    deleteQmrAssets,
    qmrActions,
    updateQmrRestrictedAssetIds,
} from '@packages/contexts/qmrs';
import AssetEditorModal from '@web/qmr/components/asset-editor-modal';
import classNames from 'classnames';
import colors from '@packages/core/styles/colors';
import ConfirmDeleteAssetModal from './confirm-delete-asset-modal';

const logger = getLogger('qmr');

const useAttachmentsStyles = createUseStyles({
    dropZoneArea: {
        height: 80,
        marginRight: 24,
        paddingLeft: 24,
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        border: `2px dashed ${colors.grayThree}`,
    },
    attachmentThumbnail: {
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: '50% 50%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
        '&:hover': {
            opacity: 0.8,
        },
    },
    attachmentNameUnderline: {
        cursor: 'pointer',
        maxWidth: '250px',
        flexShrink: 1,
        minWidth: 0,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    assetPopover: {
        minWidth: 175,
        backgroundColor: colors.white,
        border: `1px solid ${colors.grayThree}`,
        borderRadius: 4,
    },
    assetPopoverSection: {
        padding: '4px 0',
    },
    downloadAssetBtn: {
        '&:hover': {
            textDecoration: 'none',
        },
    },
    lockOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        alignItems: 'center',
        justifyContent: 'center',
        display: 'flex',
        backgroundColor: 'rgba(0,0,0,0.65)',
    },
});

interface AttachmentsProps {
    toggle: () => void;
}

const Attachments = (props: AttachmentsProps) => {
    useQmrAssetPolling(assetUploadsService);

    const { toggle } = props;
    const { t } = useTranslation();
    const { qmr } = useQmrState();
    const { abortSignalRef } = useAbortController();
    const classes = useAttachmentsStyles();
    const assetUploadsDispatch = useAssetUploadsDispatch();
    const qmrDispatch = useQmrDispatch();
    const [showToast, setShowToast] = useState<{ show: boolean; message: string }>({ show: false, message: '' });
    const [isDeletedLoading, setIsDeletedLoading] = useState(false);
    const qmrAssetDetails = getQmrAssetDetails(qmr);
    const maxAssetLimit = qmr?.maxAssetLimit != undefined ? qmr.maxAssetLimit : MAX_ATTACHMENT_COUNT;
    const isMax = (qmr?.assets?.length ?? 0) >= maxAssetLimit;

    const [viewOnly, setViewOnly] = useState(true);
    const [editorAssetIndex, setEditorAssetIndex] = useState(-1);
    const [assetProgress, setAssetProgress] = useState<{
        [assetId: string]: {
            uploadId: string;
            status: string;
            progress: number;
        };
    }>({});
    const [popoverId, setPopoverId] = useState<string | null>(null);
    const [showDeleteAssetModal, setShowDeleteAssetModal] = useState(false);
    const confirmDeleteAssetCallbackRef = useRef<() => Promise<any>>();
    const noOfAttachmentDescription =
        qmr?.assets?.length === 1 ? 'qmr:sections.media.description' : 'qmr:sections.media.description_plural';

    const getFileNameAndExtension = (file: String) => {
        const splitFileName = file.split('.');
        const fileExtn = splitFileName[splitFileName.length - 1];
        const fileName = splitFileName.slice(0, splitFileName.length - 1).join('.');
        const cleanedName = fileName.replace(/\(\d+\)$/, '');
        return { name: cleanedName, extension: fileExtn };
    };
    const pushNotificationHandler = useCallback(
        (notification: any) => {
            if (!qmr || !Array.isArray(qmr.assets) || qmr.assets.length === 0) {
                return;
            }

            if (notification.assetChangedTypeId === 'ASSET_PROCESSING_STATE') {
                const assetBelongsToQmr =
                    qmr.assets && qmr.assets.findIndex((a) => a.assetId === notification.assetId) > -1;

                if (!assetBelongsToQmr) {
                    return;
                }

                assetUploadsService
                    .getAsset({
                        assetId: notification.assetId,
                        ignoreCache: true,
                        signal: abortSignalRef.current,
                    })
                    .then((response) => {
                        if (!response.success && response.aborted) {
                            return;
                        } else if (!response.success) {
                            throw response.data;
                        }

                        qmrDispatch(qmrActions.updateAsset({ asset: response.data.asset }));
                    })
                    .catch((e: Error) => {
                        logger.error(`Failed to fetch the asset [${notification.assetId}]`, {
                            error: e,
                            assetId: notification.assetId,
                        });
                    });
            }
        },
        [abortSignalRef, qmr, qmrDispatch]
    );

    usePushNotifications(pushNotificationHandler);

    const onFilesDrop = useCallback(
        async (filesToUpload: File[]) => {
            if (!qmr) {
                return;
            }

            const currentCount = qmr?.assets?.length || 0;
            const remaining = maxAssetLimit - currentCount;

            if (remaining <= 0) {
                alert(`Maximum of ${maxAssetLimit} attachments reached.`);
                return;
            }

            const files = filesToUpload.slice(0, remaining);

            const duplicateMap = new Map<string, number>();

            qmr.assets?.forEach((asset) => {
                const fileName = getFileNameAndExtension(asset.name).name;
                const count = duplicateMap.get(fileName) || 0;
                duplicateMap.set(fileName, count + 1);
            });
            if (filesToUpload.length > remaining) {
                alert(`Only ${remaining} attachment(s) allowed.`);
                return;
            }

            let inValidFileName = false;
            files.map((file) => {
                let splitFileName = file.name && split(file.name, '.');
                // The correct way to get the file extension/name is by getting the last time a period occurs in the file name, not the first time. This can cause errors in file names such as 'comment.left.today.image.sep.8.png'
                let fileExtn = splitFileName[splitFileName.length - 1];

                if (!splitFileName[0]) {
                    inValidFileName = true;
                    alert('The file name should not be empty');
                } else if (fileExtn === '' || fileExtn === file.name) {
                    inValidFileName = true;
                    alert('Please add a file extension to the file name.');
                }
                return inValidFileName;
            });
            if (!inValidFileName) {
                const assetUploads: AssetUploadInfo[] = files.map((file) => {
                    let assetTypeId: AssetType;
                    // The browser doesn't return file type of `heic` files on windows
                    // so manually check file extension and set assetTypeId accordingly
                    if (!file.type) {
                        assetTypeId = file.name.toLowerCase().endsWith('.heic') ? AssetType.Image : AssetType.File;
                    } else {
                        assetTypeId = file.type.includes('video')
                            ? AssetType.Video
                            : file.type.includes('image')
                              ? AssetType.Image
                              : AssetType.File;
                    }
                    const { name, extension } = getFileNameAndExtension(file.name);
                    const currentCount = duplicateMap.get(name) || 0;

                    duplicateMap.set(name, currentCount + 1);
                    return {
                        assetTypeId,
                        assetDisposition: {
                            assetDispositionId: 'QMR',
                            qmrId: qmr.qmrId,
                        },
                        name: currentCount > 0 ? `${name}(${currentCount}).${extension}` : `${name}.${extension}`,
                        assetUri: file,
                        sizeInBytes: file.size,
                        contentType: file.type,
                        eventHandlers: {
                            onCompleted: (uploadId: string, asset: Asset) => {
                                const { [asset.assetId]: removed, ...rest } = assetProgress;
                                setAssetProgress({ ...rest });

                                assetUploadsDispatch(
                                    assetUploadsActions.updateTask({
                                        uploadId,
                                        status: AssetUploadTaskStatus.Completed,
                                    })
                                );

                                qmrDispatch(
                                    qmrActions.updateAsset({
                                        asset: { ...asset, assetProcessingStateId: AssetProcessingState.Processing },
                                    })
                                );
                            },
                            onProgress: (uploadId: string, asset: Asset, progress: number) => {
                                httpClient.refreshToken();

                                setAssetProgress({
                                    ...assetProgress,
                                    [asset.assetId]: {
                                        ...assetProgress[asset.assetId],
                                        progress,
                                    },
                                });
                            },
                            onError: (uploadId: string, asset: Asset, error: string) => {
                                const errorMetaData = buildErrorMetaData({
                                    err: error,
                                    component: 'QMR_Attachments',
                                    metadata: {
                                        assetId: asset.assetId,
                                        assetProcessingStateId: AssetProcessingState.Error,
                                        assetTypeId: asset.assetTypeId,
                                    },
                                });

                                updateAssetProcessingStatus({
                                    assetId: asset.assetId,
                                    assetProcessingStateId: AssetProcessingState.Error,
                                    assetUploadsDispatch,
                                    assetUploadsService,
                                    errorMetaData,
                                    signal: abortSignalRef.current,
                                    uploadId,
                                })
                                    .then((updatedAsset) => {
                                        if (!updatedAsset) {
                                            setShowToast({ show: true, message: errorMetaData.message as string });
                                            return;
                                        }
                                        setShowToast({
                                            show: true,
                                            message: updatedAsset.error || (errorMetaData.message as string),
                                        });
                                    })
                                    .catch((error) => {
                                        setShowToast({ show: true, message: errorMetaData.message || error.message });
                                        alert(errorMetaData.message || error.message);
                                    });
                            },
                        },
                    };
                });

                assetUploadsService.queueUploadTasks(assetUploads).then((uploadTasks) => {
                    if (uploadTasks) {
                        uploadTasks.forEach((task, index) => {
                            qmrDispatch(
                                qmrActions.updateAsset({
                                    asset: { ...task.asset, assetProcessingStateId: AssetProcessingState.Uploading },
                                })
                            );
                        });

                        assetUploadsDispatch(assetUploadsActions.addUploadTasks({ uploadTasks }));

                        setAssetProgress(
                            uploadTasks.reduce((assetProgress, task) => {
                                return {
                                    ...assetProgress,
                                    [task.asset.assetId]: {
                                        uploadId: task.uploadId,
                                        status: task.status,
                                        progress: 0,
                                    },
                                };
                            }, {})
                        );
                    }
                });
            }
        },
        [abortSignalRef, assetProgress, assetUploadsDispatch, maxAssetLimit, qmr, qmrDispatch]
    );

    const {
        getRootProps,
        getInputProps,
        open: openFileInput,
    } = useDropzone({
        onDrop: onFilesDrop,
        noClick: true,
        noKeyboard: true,
    });

    const dropZoneRootProps = getRootProps();
    const fileInputProps = getInputProps() as FormControlProps;

    const itemOptionsRenderer = (assetDetail: QmrAssetDetails, index: number) => () => {
        return (
            <Popover
                isOpen={popoverId === assetDetail.assetId}
                positions={['bottom', 'top']}
                onClickOutside={() => setPopoverId(null)}
                contentLocation={({ childRect, popoverRect, position }) => {
                    return getPopoverContentLocation(childRect, popoverRect, position, 4);
                }}
                content={
                    <div className={classes.assetPopover}>
                        <div className={classes.assetPopoverSection}>
                            <Button
                                alignTitle="left"
                                variant="ghost-gray"
                                onPress={() => {
                                    setViewOnly(true);
                                    setEditorAssetIndex(index);
                                    setPopoverId(null);
                                }}
                            >
                                <>
                                    <Icon name="eye" size={16} />
                                    <Typography style={{ paddingLeft: 12 }} variant="label">
                                        {t('buttons:view', 'View')}
                                    </Typography>
                                </>
                            </Button>

                            {assetDetail.assetProcessingStateId === AssetProcessingState.Complete &&
                                qmr?.capabilities.editMediaOnQmr && (
                                    <Button
                                        alignTitle="left"
                                        variant="ghost-gray"
                                        onPress={() => {
                                            setViewOnly(false);
                                            setEditorAssetIndex(index);
                                            setPopoverId(null);
                                        }}
                                    >
                                        <>
                                            <Icon name="pencil" size={16} />
                                            <Typography style={{ paddingLeft: 12 }} variant="label">
                                                {t('buttons:edit', 'Edit')}
                                            </Typography>
                                        </>
                                    </Button>
                                )}

                            {qmr?.capabilities.downloadAttachmentsOnQmr && (
                                <a
                                    href={getMediaUrl(assetDetail.media)}
                                    download={assetDetail.name}
                                    rel="noopener noreferrer"
                                    target="_blank"
                                    className={classes.downloadAssetBtn}
                                >
                                    <Button
                                        alignTitle="left"
                                        variant="ghost-gray"
                                        onPress={() => {
                                            setPopoverId(null);
                                        }}
                                    >
                                        <>
                                            <Icon name="download" size={16} />
                                            <Typography style={{ paddingLeft: 12 }} variant="label">
                                                {t('buttons:download', 'Download')}
                                            </Typography>
                                        </>
                                    </Button>
                                </a>
                            )}

                            {qmr?.capabilities.markRestrictedMediaOnQmr && (
                                <Button
                                    alignTitle="left"
                                    variant="ghost-gray"
                                    onPress={() => {
                                        setPopoverId(null);
                                        let restrictedAssetIds = qmr?.restrictedAssetIds || [];

                                        if (assetDetail.isRestricted) {
                                            restrictedAssetIds = restrictedAssetIds.filter(
                                                (id) => id !== assetDetail.assetId
                                            );
                                        } else {
                                            restrictedAssetIds = uniq([...restrictedAssetIds, assetDetail.assetId]);
                                        }

                                        updateQmrRestrictedAssetIds({
                                            qmrId: qmr.qmrId,
                                            restrictedAssetIds,
                                            qmrDispatch,
                                            qmrsService,
                                        }).catch((e) => {
                                            alert(e.message);
                                        });
                                    }}
                                >
                                    <>
                                        <Icon name="lock-closed" size={16} />
                                        <Typography style={{ paddingLeft: 12 }} variant="label">
                                            {assetDetail.isRestricted
                                                ? t('buttons:unrestrictMedia', 'Mark as unrestricted')
                                                : t('buttons:restrictMedia', 'Mark as restricted')}
                                        </Typography>
                                    </>
                                </Button>
                            )}
                        </div>

                        <hr />

                        {qmr?.capabilities.deleteAssetFromQmr && (
                            <div className={classes.assetPopoverSection}>
                                <Button
                                    alignTitle="left"
                                    variant="ghost-gray"
                                    onPress={() => {
                                        setPopoverId(null);
                                        setShowDeleteAssetModal(true);
                                        setIsDeletedLoading(false);
                                        confirmDeleteAssetCallbackRef.current = () => {
                                            if (!qmr) {
                                                return Promise.resolve();
                                            }

                                            return deleteQmrAssets({
                                                qmrId: qmr.qmrId,
                                                assetIds: [assetDetail.assetId],
                                                qmrsService,
                                                qmrDispatch,
                                                signal: abortSignalRef.current,
                                            }).catch((error) => {
                                                alert(error.message);
                                            });
                                        };
                                    }}
                                >
                                    <>
                                        <Icon name="trash" size={16} color="redOne" />
                                        <Typography style={{ paddingLeft: 12 }} variant="label" color="redOne">
                                            {t('buttons:delete', 'Delete')}
                                        </Typography>
                                    </>
                                </Button>
                            </div>
                        )}
                    </div>
                }
            >
                <div>
                    <Button
                        active={popoverId === assetDetail.assetId}
                        variant="ghost-blue"
                        onPress={() => setPopoverId(popoverId === assetDetail.assetId ? null : assetDetail.assetId)}
                    >
                        <Icon name="more-dots-vertical" />
                    </Button>
                </div>
            </Popover>
        );
    };

    return (
        <>
            <Toaster
                show={showToast.show}
                onClose={() => setShowToast({ show: false, message: '' })}
                body={showToast.message}
                styles={{ backgroundColor: colors.black }}
                textColor="white"
            />
            {qmr?.capabilities.deleteAssetFromQmr && (
                <ConfirmDeleteAssetModal
                    show={showDeleteAssetModal}
                    onConfirm={() => {
                        if (confirmDeleteAssetCallbackRef.current) {
                            setIsDeletedLoading(true);
                            confirmDeleteAssetCallbackRef.current().finally(() => {
                                setShowDeleteAssetModal(false);
                            });
                        }
                    }}
                    onHide={() => {
                        setShowDeleteAssetModal(false);
                        setIsDeletedLoading(false);
                        confirmDeleteAssetCallbackRef.current = undefined;
                    }}
                    isLoading={isDeletedLoading}
                />
            )}
            {editorAssetIndex > -1 && qmr?.capabilities.viewMediaOnQmr && (
                <AssetEditorModal
                    viewOnly={viewOnly}
                    toggleEdit={() => setViewOnly(!viewOnly)}
                    assetIndex={editorAssetIndex}
                    onClose={() => {
                        setEditorAssetIndex(-1);
                        setPopoverId(null);
                    }}
                    toggle={toggle}
                    onAssetUpdated={(updatedAsset) => {
                        qmrDispatch(qmrActions.updateAsset({ asset: updatedAsset }));
                    }}
                />
            )}

            <Row>
                <Col>
                    <div className="d-flex align-items-center justify-content-between mb-4">
                        <h4 className="mb-0">{t('qmr:sections.media.title')}</h4>
                        <p className="mb-0 text-muted">
                            {t('qmr:sections.media.attachmentsUsed', {
                                count: qmr?.assets?.length ?? 0,
                                limit: maxAssetLimit,
                            })}
                        </p>
                        <p className="mb-0 text-muted">
                            {t(`${noOfAttachmentDescription}`, {
                                count: qmr?.assets?.length,
                                totalSize:
                                    qmr?.assets
                                        ?.reduce((acc, asset) => {
                                            const originalMedia = asset.media.find(
                                                (m) =>
                                                    ((m as any).assetImageTypeId || (m as any).assetVideoTypeId) ===
                                                        AssetMediaType.Original || 'assetFileId' in m
                                            );

                                            return (acc += (originalMedia?.sizeInBytes ?? 0) / 1000);
                                        }, 0)
                                        .toFixed(1) + 'KB',
                            })}
                        </p>
                    </div>
                </Col>
            </Row>

            {qmr?.capabilities.attachAssetToQmr && qmr?.capabilities.editQmr && (
                <Row>
                    <Col>
                        <div
                            className="d-flex align-items-center justify-content-start flex-wrap"
                            {...dropZoneRootProps}
                        >
                            <div className={classNames('mt-4 mb-0', classes.dropZoneArea)}>
                                {t('qmr:sections.media.instructions')}
                                &nbsp;
                                <Button
                                    variant="link"
                                    onPress={() => {
                                        openFileInput();
                                    }}
                                    title={t('qmr:sections.media.uploadLink', 'upload')}
                                    disabled={isMax}
                                />
                            </div>

                            <Form.Group
                                controlId="attachments"
                                className="mt-4 mb-0"
                                onClick={() => {
                                    openFileInput();
                                }}
                                style={{
                                    opacity: isMax ? 0.5 : 1,
                                    pointerEvents: isMax ? 'none' : 'auto',
                                }}
                            >
                                <Form.Control disabled={isMax} as="input" {...fileInputProps} />
                                <Form.Label />
                            </Form.Group>
                        </div>
                    </Col>
                </Row>
            )}

            {qmrAssetDetails.map((assetDetail, index) => {
                return (
                    <QmrAssetListItem
                        key={assetDetail.assetId}
                        assetTypeId={assetDetail.assetTypeId}
                        title={
                            <div
                                onClick={() => {
                                    setViewOnly(true);
                                    setEditorAssetIndex(index);
                                }}
                                className={classes.attachmentNameUnderline}
                            >
                                {assetDetail.name}
                            </div>
                        }
                        description={assetDetail.note}
                        createdTimestampDescription={assetDetail.createdTimestampDescription}
                        progress={assetProgress[assetDetail.assetId]?.progress}
                        moreOptionsRenderer={itemOptionsRenderer(assetDetail, index)}
                        isRestricted={assetDetail.isRestricted}
                        isAdditionalUpload={assetDetail.isAdditionalUpload}
                        thumbnailRenderer={(size) => {
                            const showThumbnailImage = !assetDetail.thumbnailMedia?.iconName;
                            const thumbnailSrc = showThumbnailImage ? assetDetail.thumbnailMedia?.uri : undefined;
                            const backgroundImage = showThumbnailImage
                                ? thumbnailSrc && `url(${thumbnailSrc})`
                                : undefined;

                            return (
                                <div
                                    style={{
                                        backgroundImage,
                                        ...size,
                                    }}
                                    className={classes.attachmentThumbnail}
                                    onClick={() => {
                                        setEditorAssetIndex(index);
                                        setViewOnly(true);
                                    }}
                                >
                                    {showThumbnailImage
                                        ? !thumbnailSrc && <ActivityIndicator color={colors.blueOne} size="large" />
                                        : !assetDetail.isRestricted && (
                                              <Icon
                                                  name={(assetDetail.thumbnailMedia?.iconName || '') as StsIconName}
                                              />
                                          )}
                                    {assetDetail.isRestricted && (
                                        <div className={classes.lockOverlay}>
                                            <Icon name="lock-closed" color="white" />
                                        </div>
                                    )}
                                </div>
                            );
                        }}
                        onCancelUpload={() => {}}
                    />
                );
            })}
        </>
    );
};

export default Attachments;
